import React from 'react';

import { LTLoader, LTText } from '@lambdatestincprivate/lt-components';
import { themeGet } from '@primer/react';
import capitalize from 'lodash/capitalize';
import styled from 'styled-components';

import LTTestCard from '../LTTestCard';
import phone from '../../assets/phone.svg';
import { getStatusIconAndClassPrefix } from '../../utils/statusOptions';
import { isMobileDevice } from '../../utils/testHelpers';
import {
  getBrowserIcon,
  getOsIcon,
  truncateTextFromMiddle,
} from '../../utils/utilityFunctions';
import {
  convertTimeTaken,
  timeDifference,
  timeSince,
} from '../../utils/dateTimeUtils';

const TestList = styled.div`
  max-height: calc(100vh - 260px);
  overflow-y: auto;
`;

const ListContainer = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled.li`
  border-left: 1px solid ${themeGet('colors.border.default')};
  border-right: 1px solid ${themeGet('colors.border.default')};
  border-bottom: 1px solid ${themeGet('colors.border.default')};

  &:first-child {
    border-top: 1px solid ${themeGet('colors.border.default')};
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }

  &:last-child {
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

const StyledLink = styled.a`
  color: inherit;
  text-decoration: none;
  display: block;

  &:hover {
    color: inherit;
  }

  &:focus-visible > div {
    outline: 2px solid #0969da;
    outline-offset: -1px;
  }

  /* Remove LTTestCard's own border-bottom since ListItem handles borders */
  > div {
    border-bottom: none;
  }
`;

const NoDataWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
`;

const RetryList = ({ data = {}, isLoading = false }) => {
  const { tests: testList = [] } = data;

  if (isLoading) {
    return <LTLoader heading="Loading Attempts" />;
  }

  if (!testList || testList.length === 0) {
    return (
      <NoDataWrapper>
        <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR} color="fg.muted">
          No attempts found
        </LTText>
      </NoDataWrapper>
    );
  }

  return (
    <TestList>
      <ListContainer>
        {testList.map((test, index) => {
          let {
            name = '',
            id = '',
            browser = '',
            category = '',
            startedAt = '',
            endedAt = '',
            device = '',
            osName = '',
            osVersion = '',
            version = '',
            status = '',
            createdAt = '',
            reason = '',
            tags = [],
          } = test || {};

          let browserIcon = getBrowserIcon(browser);
          let osIcon = getOsIcon(category);
          const { statusIconName = '' } = getStatusIconAndClassPrefix(status);
          const mobile = isMobileDevice(test);

          if (mobile) {
            browserIcon = phone;
            version = device;
          }

          const timeTaken = convertTimeTaken(
            timeDifference(startedAt, endedAt, true),
          );
          const testTimeSince = timeSince(createdAt, Date.now(), false, false);
          const testName = name || id;
          const truncatedReason = truncateTextFromMiddle(reason || '', 90);

          const totalTags = Array.isArray(tags) ? tags.length : 0;
          const remainingTagCount = totalTags > 2 ? totalTags - 2 : 0;
          const displayedTagsText = Array.isArray(tags)
            ? tags.slice(0, 2).map((t) => t?.name).join(' ')
            : '';

          return (
            <ListItem key={id || index}>
              <span className="hiddenFromView" id={`lt-retry-card-${index}`}>
                {`${testName}, Test Status-${status}, ${
                  displayedTagsText || ''
                } ${
                  remainingTagCount > 0 ? `+${remainingTagCount} tags` : ''
                } ${timeTaken ? `Test Time ${timeTaken}, ` : ''}on ${capitalize(
                  browser,
                )}, Version ${capitalize(osName)}, ${testTimeSince} Press Enter to view`}
              </span>
              <StyledLink
                aria-labelledby={`lt-retry-card-${index}`}
                href={`/test?testID=${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  window.open(`/test?testID=${id}`, '_blank');
                }}
              >
                <LTTestCard
                  browser={mobile ? 'Device' : browser}
                  browserIcon={browserIcon}
                  index={index}
                  isThemeEnabled
                  os={category}
                  osIcon={osIcon}
                  osName={osName}
                  osVersion={
                    category === 'linux' || category === 'ubuntu'
                      ? 'Linux'
                      : osVersion
                  }
                  reason={truncatedReason}
                  tags={tags}
                  testId={id}
                  testName={testName}
                  testStatus={status}
                  testStatusIcon={statusIconName}
                  testTimeSince={testTimeSince}
                  timeTaken={timeTaken}
                  version={version}
                />
              </StyledLink>
            </ListItem>
          );
        })}
      </ListContainer>
    </TestList>
  );
};

export default RetryList;
